// src/components/assistant/AgentsView.tsx
//
// Vista "Tus agentes" del shell: lista + crear + borrar agentes personalizados.
// Backend: mcp_django/apps/assistants (via /modules/assistants/api). La definición
// del agente vive en Django; el chat lo consume como capa <agent>. Ver docs/65.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Bot, Plus, Trash2, Loader2, Users, Lock, Share2 } from 'lucide-react';

import {
  createAssistant,
  deleteAssistant,
  listAssistants,
} from '@/modules/assistants/api';
import type { AssistantOut, AssistantVisibility } from '@/modules/assistants/types';

const VISIBILITY_META: Record<AssistantVisibility, { label: string; icon: JSX.Element }> = {
  personal: { label: 'Personal', icon: <Lock size={12} /> },
  org: { label: 'Organización', icon: <Users size={12} /> },
  shared: { label: 'Compartido', icon: <Share2 size={12} /> },
};

export interface AgentDraft {
  name: string;
  instructions: string;
  icon?: string;
  /** cambia en cada elección para volver a prellenar aunque sea la misma plantilla */
  nonce: number;
}

export function AgentsView({
  startCreating = false,
  draft,
}: { startCreating?: boolean; draft?: AgentDraft | null } = {}) {
  const [agents, setAgents] = useState<AssistantOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(startCreating);
  const [saving, setSaving] = useState(false);

  // form
  const [name, setName] = useState('');
  const [instructions, setInstructions] = useState('');
  const [icon, setIcon] = useState('');
  const [visibility, setVisibility] = useState<AssistantVisibility>('personal');

  // Plantilla elegida desde fuera: SOLO prellena el formulario existente; el
  // usuario revisa y guarda (nada se crea solo).
  const formRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!draft) return;
    setName(draft.name);
    setInstructions(draft.instructions);
    setIcon(draft.icon ?? '');
    setCreating(true);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      formRef.current?.querySelectorAll<HTMLInputElement>('input')[1]?.focus();
    }, 50);
  }, [draft?.nonce]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setAgents(await listAssistants('all'));
    } catch (e: any) {
      setError(e?.message || 'No se pudieron cargar los agentes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const resetForm = () => {
    setName('');
    setInstructions('');
    setIcon('');
    setVisibility('personal');
    setCreating(false);
  };

  const onCreate = async () => {
    if (!name.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      await createAssistant({
        name: name.trim(),
        instructions: instructions.trim(),
        icon: icon.trim(),
        visibility,
      });
      resetForm();
      await load();
    } catch (e: any) {
      setError(e?.message || 'No se pudo crear el agente');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (a: AssistantOut) => {
    if (!confirm(`¿Eliminar el agente "${a.name}"?`)) return;
    try {
      await deleteAssistant(a.id);
      setAgents((prev) => prev.filter((x) => x.id !== a.id));
    } catch (e: any) {
      setError(e?.message || 'No se pudo eliminar');
    }
  };

  return (
    <div className="h-full overflow-y-auto overscroll-contain bg-background text-foreground [-webkit-overflow-scrolling:touch]">
      <div className="mx-auto max-w-3xl px-4 py-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Tus agentes</h2>
            <p className="text-sm text-muted-foreground">
              Especialistas con instrucciones propias. Elígelos en el chat.
            </p>
          </div>
          {!creating && (
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="flex items-center gap-1.5 rounded-xl bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
            >
              <Plus size={16} /> Nuevo agente
            </button>
          )}
        </div>

        {error && (
          <p className="mb-4 rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</p>
        )}

        {/* Form de creación */}
        {creating && (
          <div ref={formRef} className="mb-6 rounded-2xl border border-border bg-card p-4">
            <div className="flex flex-col gap-3">
              <div className="flex gap-2">
                <input
                  value={icon}
                  onChange={(e) => setIcon(e.target.value.slice(0, 4))}
                  placeholder="🤖"
                  className="w-14 rounded-xl border border-border bg-background px-3 py-2 text-center text-lg outline-none focus:ring-2 focus:ring-ring"
                />
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nombre del agente (ej. Abogado laboral)"
                  className="flex-1 rounded-xl border border-border bg-background px-3 py-2 text-base outline-none focus:ring-2 focus:ring-ring sm:text-sm"
                />
              </div>
              <textarea
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                rows={5}
                placeholder="Instrucciones / personalidad: quién es, cómo responde, qué reglas sigue. (No repitas el formato de respuesta — de eso ya se encarga el sistema.)"
                className="resize-y rounded-xl border border-border bg-background px-3 py-2 text-base outline-none focus:ring-2 focus:ring-ring sm:text-sm"
              />
              <div className="flex items-center justify-between gap-2">
                <label className="flex items-center gap-2 text-sm text-muted-foreground">
                  Visibilidad:
                  <select
                    value={visibility}
                    onChange={(e) => setVisibility(e.target.value as AssistantVisibility)}
                    className="rounded-lg border border-border bg-background px-2 py-1 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="personal">Solo yo</option>
                    <option value="org">Toda la organización</option>
                  </select>
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="rounded-xl border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={() => void onCreate()}
                    disabled={!name.trim() || saving}
                    className="flex items-center gap-1.5 rounded-xl bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-40"
                  >
                    {saving && <Loader2 size={14} className="animate-spin" />}
                    Crear agente
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Lista */}
        {loading ? (
          <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
            <Loader2 size={16} className="animate-spin" /> Cargando…
          </div>
        ) : agents.length === 0 && !creating ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Bot size={24} className="text-primary" />
            </div>
            <p className="text-sm text-muted-foreground">
              Aún no tienes agentes. Crea tu primer especialista.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {agents.map((a) => {
              const vis = VISIBILITY_META[a.visibility] ?? VISIBILITY_META.personal;
              return (
                <div
                  key={a.id}
                  className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-lg">
                    {a.icon || <Bot size={18} className="text-muted-foreground" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium">{a.name}</span>
                      <span className="flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                        {vis.icon} {vis.label}
                      </span>
                    </div>
                    {a.instructions && (
                      <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{a.instructions}</p>
                    )}
                  </div>
                  {a.is_owner && (
                    <button
                      type="button"
                      onClick={() => void onDelete(a)}
                      aria-label="Eliminar"
                      className="shrink-0 rounded-lg p-2 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default AgentsView;
